import React from 'react';
import { useCart } from '../context/CartContext';
import { ShoppingCart, Bitcoin } from 'lucide-react';
import { formatCurrency, formatBitcoin } from '../utils/formatters';

interface CartBarProps {
  bitcoinPrice: number;
}

export const CartBar: React.FC<CartBarProps> = ({ bitcoinPrice }) => {
  const { state, toggleCart, getTotalPrice, getTotalItems } = useCart();
  const totalBitcoin = bitcoinPrice ? getTotalPrice() / bitcoinPrice : 0;

  // Don't show the bar if cart is empty or if cart drawer is open
  if (state.items.length === 0 || state.isOpen) return null;

  return (
    <div 
      className="sticky bottom-0 left-0 right-0 z-[9999] p-4 pointer-events-none"
      style={{ 
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        padding: '1rem',
        willChange: 'transform',
        transform: 'translateZ(0)',
        maxHeight: '100vh',
      }}
    >
      <div 
        className="container mx-auto max-w-5xl pointer-events-auto"
        style={{ 
          backfaceVisibility: 'hidden',
          perspective: 1000,
          position: 'relative',
        }}
      >
        <div 
          className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl mx-4 overflow-hidden transform transition-all duration-300 ease-in-out"
          style={{
            boxShadow: '0 -8px 32px rgba(0, 0, 0, 0.12), 0 8px 32px rgba(0, 0, 0, 0.12)',
            WebkitBackdropFilter: 'blur(8px)',
            position: 'relative',
          }}
        >
          <div className="px-6 py-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              {/* Item Preview */}
              <div 
                className="flex items-center gap-2 cursor-pointer hover:opacity-75 transition-opacity group"
                onClick={toggleCart}
              >
                <div className="flex -space-x-3 transform group-hover:translate-x-1 transition-transform duration-300">
                  {state.items.slice(0, 3).map((item) => (
                    <div
                      key={item.name}
                      className="w-10 h-10 rounded-full border-2 border-white overflow-hidden shadow-sm transform transition-transform duration-300 hover:scale-110"
                    >
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                  {state.items.length > 3 && (
                    <div className="w-10 h-10 rounded-full bg-orange-50 border-2 border-white flex items-center justify-center text-xs font-medium text-orange-500 shadow-sm">
                      +{state.items.length - 3}
                    </div>
                  )}
                </div>
                <div className="ml-2">
                  <div className="text-sm font-medium">
                    {getTotalItems()} {getTotalItems() === 1 ? 'item' : 'items'}
                  </div>
                </div>
              </div>

              {/* Price and Checkout */}
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="text-sm font-medium">{formatCurrency(getTotalPrice())}</div>
                  <div className="flex items-center gap-1 text-orange-500 text-sm">
                    <Bitcoin className="w-3 h-3" />
                    <span>{formatBitcoin(totalBitcoin)} BTC</span>
                  </div>
                </div>
                <button
                  onClick={toggleCart}
                  className="px-6 py-2.5 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-all duration-300 transform hover:scale-105 hover:shadow-lg active:scale-95 flex items-center gap-2"
                >
                  <ShoppingCart className="w-4 h-4" />
                  <span className="font-medium">View Cart</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Safe Area for Mobile */}
      <div className="h-[env(safe-area-inset-bottom)]" />
    </div>
  );
}; 